// src/shared/http/api-client.js
//
// Main Axios instance — replaces the default export of the original
// http-common.js.  Same base URL logic, same interceptor behaviour.

import axios from 'axios';
import { TokenService } from './token';
import { IframeUtil } from './iframe';
import { AuthErrorHandler } from './error-handler';

export const CONFIG = {
  SERVER_PATH: '/api/',
  AUTH_HEADER: 'Authorization',
  BEARER_PREFIX: 'Bearer ',
};

const apiClient = axios.create({
  baseURL: CONFIG.SERVER_PATH,
  headers: { 'Content-type': 'application/json' },
  withCredentials: true,
});

const TOKEN_REFRESH_WINDOW_SECONDS = 15 * 60; // refresh token when <= 15 min remains
let refreshPromise = null;

// Lazy-load ToastService to avoid pulling PrimeVue into the HTTP layer eagerly.
const showToast = (severity, summary, detail) => {
  import('@/shared/composables/useToast')
    .then(({ showToast: notify }) => notify(severity, summary, detail))
    .catch(() => { /* ToastService not available yet — swallow silently */ });
};

const shouldSkipRefresh = (url = '') => (
  url.includes('/login') || url.includes('/auth/refresh')
);

const syncTokenToStores = async (token) => {
  try {
    const { default: store } = await import('@/store');
    if (store?.hasModule?.('auth')) {
      store.commit('auth/SET_TOKEN', token);
    }
    // Keep legacy root token in sync for mixed old/new code paths.
    store?.commit?.('setToken', token);
  } catch {
    // Store may not be initialized yet; TokenService remains source of truth.
  }
};

const refreshAccessToken = async (currentToken) => {
  if (refreshPromise) return refreshPromise;

  refreshPromise = (async () => {
    const response = await axios.post(
      `${CONFIG.SERVER_PATH}auth/refresh`,
      null,
      {
        withCredentials: true,
        headers: {
          [CONFIG.AUTH_HEADER]: CONFIG.BEARER_PREFIX + currentToken,
        },
      },
    );

    const newToken = response?.data?.token;
    if (!newToken) {
      throw new Error('No token returned from refresh endpoint');
    }

    TokenService.setToken(newToken);
    await syncTokenToStores(newToken);
    return newToken;
  })();

  try {
    return await refreshPromise;
  } finally {
    refreshPromise = null;
  }
};

// ── Request interceptor: attach token and refresh near expiry ───────
apiClient.interceptors.request.use(
  async (config) => {
    let token = TokenService.getToken();
    if (token) {
      const requestUrl = config?.url || '';
      if (!shouldSkipRefresh(requestUrl) && TokenService.isTokenExpired()) {
        if (IframeUtil.isIframe()) {
          IframeUtil.postTokenExpired();
        } else {
          AuthErrorHandler.handleAuthError();
        }
        return Promise.reject(new Error('Token expired'));
      }

      if (!shouldSkipRefresh(requestUrl)) {
        const remaining = TokenService.getTokenRemainingTime();
        if (remaining > 0 && remaining <= TOKEN_REFRESH_WINDOW_SECONDS) {
          try {
            token = await refreshAccessToken(token);
          } catch {
            if (IframeUtil.isIframe()) {
              IframeUtil.postTokenExpired();
            } else {
              AuthErrorHandler.handleAuthError();
            }
            return Promise.reject(new Error('Token refresh failed'));
          }
        }
      }

      config.headers[CONFIG.AUTH_HEADER] = CONFIG.BEARER_PREFIX + token;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ── Response interceptor: 401/403, network & server errors ──────────
apiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    if (axios.isCancel(error) || error.name === 'AbortError') {
      return Promise.reject(error);
    }

    const isLoginRequest = error.config?.url?.includes('/login');
    if (!isLoginRequest && AuthErrorHandler.isAuthError(error)) {
      AuthErrorHandler.handleAuthError(error);
      return Promise.reject(error);
    }

    if (!error.response) {
      if (!IframeUtil.isIframe()) {
        showToast(
          'error',
          'Network Error',
          'Unable to connect to the server. Please check your internet connection.',
        );
      } else {
        IframeUtil.debounceNotify('ERROR', {
          message: 'Network error. Please check your connection.',
        });
      }
    }

    if (error.response && error.response.status >= 500) {
      if (!IframeUtil.isIframe()) {
        showToast(
          'error',
          'Server Error',
          'Something went wrong on our end. Please try again later.',
        );
      } else {
        IframeUtil.debounceNotify('ERROR', {
          message: 'Server error. Please try again later.',
        });
      }
    }

    return Promise.reject(error);
  },
);

export default apiClient;
